import {
  areaName,
  INSTALL_METHOD_LABELS,
  ROUTE_CATEGORY_LABELS,
  ROUTE_MODE_LABELS,
  ROUTE_STATUS_LABELS,
  subject,
  type PolePreview,
  type RouteDetail,
} from '@ftthnms/shared';
import { Badge, Button, RegionChip, type BadgeTone } from '@ftthnms/ui';
import { useQueryClient } from '@tanstack/react-query';
import {
  Crosshair,
  Eye,
  PenLine,
  Pencil,
  RefreshCw,
  RotateCcw,
  Route as RouteIcon,
  Trash2,
  UtilityPole,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { AssetGlyph } from '../map/AssetGlyph';
import { dateTime, metres, nf, rupiah, send } from '../lib/api';
import { colorName } from '../lib/colors';
import { invalidateAssets } from '../lib/assets';
import { useRoute, useRouteHistory } from '../lib/routes';
import { useSession } from '../lib/session';
import { ConfirmDialog } from './ConfirmDialog';
import { Drawer } from './Drawer';
import { useToast } from './Toast';

export const ROUTE_STATUS_TONE: Record<string, BadgeTone> = {
  installed: 'success',
  planned: 'warning',
  maintenance: 'danger',
};

/** Cable route detail (RTE-04..09): lengths, cost, ends, auto poles and history. */
export function RouteDrawer({
  id,
  onClose,
  onEdit,
  onOpenAsset,
  onEditShape,
  onFocus,
  onPolesPreview,
  onDeleted,
}: {
  id: string;
  onClose: () => void;
  onEdit: (r: RouteDetail) => void;
  onOpenAsset: (id: string) => void;
  onEditShape?: (r: RouteDetail) => void;
  onFocus?: (r: RouteDetail) => void;
  onPolesPreview?: (poles: PolePreview['poles'] | null) => void;
  onDeleted?: () => void;
}) {
  const { ability } = useSession();
  const qc = useQueryClient();
  const toast = useToast();
  const route = useRoute(id);
  const [deleting, setDeleting] = useState(false);
  const [withPoles, setWithPoles] = useState(true);
  const [recomputing, setRecomputing] = useState(false);
  const r = route.data;

  useEffect(() => () => onPolesPreview?.(null), [onPolesPreview]);

  if (!r) {
    return (
      <Drawer label="Detail jalur" title={route.error ? 'Jalur tidak tersedia' : 'Memuat…'} onClose={onClose}>
        {route.error ? (
          <div className="fn-alert fn-alert--danger" role="alert">
            <div className="fn-alert__body">{route.error.message}</div>
          </div>
        ) : (
          <span className="fn-spinner" aria-label="Memuat" />
        )}
      </Drawer>
    );
  }

  const target = subject('Route', { areaCode: r.areaCode });
  const canTech = !r.deletedAt && ability.can('update', target);
  const canEdit = !r.deletedAt && (canTech || ability.can('field_update', target));
  const canDelete = ability.can('delete', target);
  const title = r.name ?? `${r.from.code} → ${r.to.code}`;

  async function recompute() {
    setRecomputing(true);
    try {
      const p = await send<{ path: [number, number][]; mode: 'road' | 'straight'; warning: string | null }>(
        'POST',
        '/api/v1/routes/preview-path',
        {
          fromAssetId: r!.from.id,
          toAssetId: r!.to.id,
          mode: 'road',
        },
      );
      if (p.mode !== 'road') throw new Error(p.warning ?? 'Rute jalan tidak ditemukan.');
      await send('PUT', `/api/v1/routes/${r!.id}/path`, { mode: 'road', path: p.path });
      await invalidateAssets(qc);
      toast({ tone: 'success', text: 'Jalur dihitung ulang mengikuti jalan.' });
    } catch (err) {
      toast({ tone: 'danger', text: (err as Error).message });
    } finally {
      setRecomputing(false);
    }
  }

  async function restore() {
    try {
      await send('POST', `/api/v1/routes/${r!.id}/restore`);
      await invalidateAssets(qc);
      toast({ tone: 'success', text: 'Jalur dipulihkan.' });
    } catch (err) {
      toast({ tone: 'danger', text: (err as Error).message });
    }
  }

  return (
    <Drawer
      label={`Detail jalur ${title}`}
      eyebrow={`Jalur ${ROUTE_CATEGORY_LABELS[r.category]}`}
      badge={
        <Badge tone={ROUTE_STATUS_TONE[r.status] ?? 'neutral'} dot>
          {ROUTE_STATUS_LABELS[r.status]}
        </Badge>
      }
      title={title}
      subtitle={ROUTE_MODE_LABELS[r.mode]}
      icon={
        <span className="fn-tile fn-tile--primary">
          <RouteIcon className="i" aria-hidden />
        </span>
      }
      onClose={onClose}
      actions={
        <>
          {onFocus && (
            <button
              type="button"
              className="fn-icon-btn fn-icon-btn--ghost fn-icon-btn--sm"
              aria-label="Fokus di peta"
              title="Fokus di peta"
              onClick={() => onFocus(r)}
            >
              <Crosshair className="i i--sm" aria-hidden />
            </button>
          )}
          {onEditShape && canTech && (
            <button
              type="button"
              className="fn-icon-btn fn-icon-btn--ghost fn-icon-btn--sm"
              aria-label="Ubah bentuk jalur"
              title="Ubah bentuk"
              onClick={() => onEditShape(r)}
            >
              <PenLine className="i i--sm" aria-hidden />
            </button>
          )}
          {canEdit && (
            <button
              type="button"
              className="fn-icon-btn fn-icon-btn--ghost fn-icon-btn--sm"
              aria-label="Edit jalur"
              title="Edit"
              onClick={() => onEdit(r)}
            >
              <Pencil className="i i--sm" aria-hidden />
            </button>
          )}
          {canDelete && !r.deletedAt && (
            <button
              type="button"
              className="fn-icon-btn fn-icon-btn--ghost fn-icon-btn--danger fn-icon-btn--sm"
              aria-label="Hapus jalur"
              title="Hapus"
              onClick={() => setDeleting(true)}
            >
              <Trash2 className="i i--sm" aria-hidden />
            </button>
          )}
        </>
      }
    >
      {r.deletedAt && (
        <div className="fn-alert fn-alert--warning" role="status">
          <div className="fn-alert__body">
            <span className="fn-alert__title">Di tempat sampah</span>
            <span className="fn-alert__text" style={{ color: 'inherit' }}>
              Dihapus {dateTime.format(new Date(r.deletedAt))}.
            </span>
          </div>
          {canDelete && (
            <Button size="sm" icon={<RotateCcw className="i" aria-hidden />} onClick={() => void restore()}>
              Pulihkan
            </Button>
          )}
        </div>
      )}

      <div className="grid gap-2" style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
        <Stat label="Panjang jalur" value={metres(r.lengthM)} />
        <Stat
          label="Panjang kabel"
          value={metres(r.cableLengthM)}
          meta={r.slackPct > 0 ? `+${r.slackPct}% cadangan` : undefined}
        />
        <Stat
          label="Tiang otomatis"
          value={nf.format(r.poles)}
          meta={
            r.autoPoleIntervalM
              ? `tiap ${r.autoPoleIntervalM} m${r.autoPoleAtTurns ? ' + tikungan' : ''}`
              : undefined
          }
        />
        <Stat
          label="Estimasi biaya"
          value={r.cost.total === null ? '—' : rupiah.format(r.cost.total)}
          meta={r.cost.poles ? `kabel ${rupiah.format(r.cost.cable ?? 0)}` : undefined}
        />
      </div>

      <section className="fn-drawer__section">
        <span className="fn-label">Ujung jalur</span>
        {[
          ['Asal', r.from],
          ['Tujuan', r.to],
        ].map(([label, end]) => {
          const e = end as RouteDetail['from'];
          return (
            <button
              key={label as string}
              type="button"
              className="fn-layer"
              style={{ padding: 0 }}
              onClick={() => onOpenAsset(e.id)}
            >
              <span className="fn-layer__glyph">
                <AssetGlyph type={e.typeCode} />
              </span>
              <span className="fn-layer__name">
                <span className="fn-truncate" title={e.name}>
                  {e.name}
                </span>
                <span className="mono fn-truncate" style={{ color: 'var(--text-muted)', fontSize: 11 }}>
                  {label as string} · {e.code}
                </span>
              </span>
              {/* Only an end in another area needs its area spelled out. */}
              {e.areaCode && e.areaCode !== r.areaCode && <RegionChip code={e.areaCode} />}
            </button>
          );
        })}
      </section>

      <dl className="fn-dl">
        <dt>Tipe kabel</dt>
        <dd>{r.cableTypeName ?? '—'}</dd>
        <dt>Jumlah core</dt>
        <dd className="num">{r.coreCount ?? '—'}</dd>
        <dt>Instalasi</dt>
        <dd>{INSTALL_METHOD_LABELS[r.installMethod]}</dd>
        <dt>Warna garis</dt>
        <dd style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {r.color ? (
            <>
              <span className="fn-swatch" style={{ background: r.color }} aria-hidden />
              {colorName(r.color)}
              <span className="mono" style={{ color: 'var(--text-muted)', fontSize: 11 }}>
                {r.color}
              </span>
            </>
          ) : (
            <span style={{ color: 'var(--text-muted)' }}>Ikut status</span>
          )}
        </dd>
        <dt>Wilayah</dt>
        <dd>{r.areaCode ? areaName(r.areaCode) : '—'}</dd>
      </dl>
      {r.notes && (
        <p className="fn-card__desc" style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
          {r.notes}
        </p>
      )}

      {canTech && r.mode === 'road' && (
        <Button
          size="sm"
          icon={<RefreshCw className="i" aria-hidden />}
          loading={recomputing}
          onClick={() => void recompute()}
          style={{ alignSelf: 'flex-start' }}
        >
          Hitung ulang mengikuti jalan
        </Button>
      )}

      {canTech && r.installMethod === 'aerial' && <AutoPoles route={r} onPreview={onPolesPreview} />}

      <RouteHistory id={r.id} />
      <p className="fn-hint">
        Dibuat {dateTime.format(new Date(r.createdAt))}
        {r.createdBy ? ` oleh ${r.createdBy}` : ''} · diubah {dateTime.format(new Date(r.updatedAt))}
      </p>

      {deleting && (
        <ConfirmDialog
          title="Hapus jalur ini?"
          icon={<Trash2 className="i" aria-hidden />}
          confirmLabel="Hapus"
          onClose={() => setDeleting(false)}
          onConfirm={async () => {
            const res = await send<{ polesRemoved: number }>(
              'DELETE',
              `/api/v1/routes/${r.id}${withPoles ? '?withPoles=1' : ''}`,
            );
            await invalidateAssets(qc);
            setDeleting(false);
            toast({
              tone: 'success',
              text: `Jalur dipindah ke tempat sampah${res.polesRemoved ? ` bersama ${res.polesRemoved} tiang otomatis` : ''}.`,
              action: {
                label: 'Urungkan',
                onClick: () =>
                  void send('POST', `/api/v1/routes/${r.id}/restore`).then(() => invalidateAssets(qc)),
              },
            });
            onDeleted?.();
          }}
        >
          <span>
            <strong>{title}</strong> ({metres(r.lengthM)}) dipindah ke tempat sampah dan bisa dipulihkan dalam
            30 hari.
          </span>
          {r.poles > 0 && (
            <label className="fn-choice" style={{ cursor: 'pointer' }}>
              <span>
                <span className="fn-choice__title">Hapus juga {r.poles} tiang otomatis</span>
                <span className="fn-choice__desc">
                  Tiang yang dibuat dari jalur ini. Tiang lain tidak tersentuh.
                </span>
              </span>
              <input
                type="checkbox"
                checked={withPoles}
                onChange={(e) => setWithPoles(e.target.checked)}
                style={{ width: 18, height: 18, flex: 'none' }}
              />
            </label>
          )}
        </ConfirmDialog>
      )}
    </Drawer>
  );
}

function Stat({ label, value, meta }: { label: string; value: string; meta?: string }) {
  return (
    <div className="fn-card" style={{ padding: '10px 12px', background: 'var(--bg-surface-inset)' }}>
      <div className="fn-kpi__label">{label}</div>
      <div className="num" style={{ font: '800 16px/22px var(--font-sans)', marginTop: 2 }}>
        {value}
      </div>
      {meta && <div className="fn-hint">{meta}</div>}
    </div>
  );
}

/** Auto poles along the route (RTE-09): preview on the map with cost, then save. */
function AutoPoles({
  route,
  onPreview,
}: {
  route: RouteDetail;
  onPreview?: (poles: PolePreview['poles'] | null) => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const [interval, setIntervalM] = useState(String(route.autoPoleIntervalM ?? 30));
  const [atTurns, setAtTurns] = useState(route.autoPoleAtTurns ?? true);
  const [preview, setPreview] = useState<PolePreview | null>(null);
  const [busy, setBusy] = useState<'preview' | 'save' | null>(null);
  const [error, setError] = useState<string | null>(null);

  const opts = () => ({ intervalM: Number(interval), atTurns });

  async function run(kind: 'preview' | 'save') {
    setBusy(kind);
    setError(null);
    try {
      if (kind === 'preview') {
        const p = await send<PolePreview>('POST', `/api/v1/routes/${route.id}/poles/preview`, opts());
        setPreview(p);
        onPreview?.(p.poles);
      } else {
        const res = await send<{ created: number; removed: number }>(
          'POST',
          `/api/v1/routes/${route.id}/poles/generate`,
          opts(),
        );
        await invalidateAssets(qc);
        setPreview(null);
        onPreview?.(null);
        toast({
          tone: 'success',
          text: `${res.created} tiang dibuat${res.removed ? `, ${res.removed} tiang otomatis lama diganti` : ''}.`,
        });
      }
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(null);
    }
  }

  return (
    <section className="fn-card" style={{ background: 'var(--bg-surface-inset)' }}>
      <div className="fn-card__header">
        <div>
          <h3 className="fn-card__title" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <UtilityPole className="i i--sm" aria-hidden /> Tiang otomatis
          </h3>
          <p className="fn-card__desc">
            Tiang tumpu di sepanjang jalur. Tiang yang sudah ada dalam 10 m dipakai ulang.
          </p>
        </div>
      </div>
      <div className="fn-card__body" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div className="fn-form-grid">
          <div className="fn-field">
            <label className="fn-label" htmlFor="pole-interval">
              Interval
            </label>
            <div className="fn-input-group">
              <input
                id="pole-interval"
                className="fn-input fn-input--mono"
                inputMode="numeric"
                value={interval}
                onChange={(e) => {
                  setIntervalM(e.target.value);
                  setPreview(null);
                  onPreview?.(null);
                }}
              />
              <span className="fn-input-group__suffix">m</span>
            </div>
            <span className="fn-hint">10–100 m</span>
          </div>
          <label className="fn-choice" style={{ cursor: 'pointer', alignSelf: 'end' }}>
            <span className="fn-choice__title">Di tikungan &gt;30°</span>
            <span className="fn-switch">
              <input
                className="fn-switch__input"
                type="checkbox"
                checked={atTurns}
                onChange={(e) => {
                  setAtTurns(e.target.checked);
                  setPreview(null);
                  onPreview?.(null);
                }}
              />
              <span className="fn-switch__track" />
            </span>
          </label>
        </div>
        {preview && (
          <div className="fn-alert fn-alert--info" role="status">
            <div className="fn-alert__body">
              <span className="fn-alert__title">
                {nf.format(preview.poles.length)} tiang baru
                {preview.totalCost !== null ? ` · ${rupiah.format(preview.totalCost)}` : ''}
              </span>
              <span className="fn-alert__text" style={{ color: 'inherit' }}>
                {preview.poles.filter((p) => p.reason === 'turn').length} di tikungan
                {preview.skippedNearExisting
                  ? ` · ${preview.skippedNearExisting} memakai tiang yang sudah ada`
                  : ''}
                {preview.replacesAuto ? ` · menggantikan ${preview.replacesAuto} tiang otomatis lama` : ''}.
                Titik ditampilkan di peta.
              </span>
            </div>
          </div>
        )}
        {error && <span className="fn-hint fn-hint--error">{error}</span>}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button
            size="sm"
            icon={<Eye className="i" aria-hidden />}
            loading={busy === 'preview'}
            onClick={() => void run('preview')}
          >
            Pratinjau
          </Button>
          <Button
            size="sm"
            variant="primary"
            disabled={!preview || preview.poles.length === 0}
            loading={busy === 'save'}
            onClick={() => void run('save')}
          >
            Simpan {preview ? nf.format(preview.poles.length) : ''} Tiang
          </Button>
        </div>
      </div>
    </section>
  );
}

const ROUTE_ACTION_LABEL: Record<string, string> = {
  'route.create': 'Dibuat',
  'route.update': 'Diubah',
  'route.path': 'Bentuk diubah',
  'route.delete': 'Dihapus',
  'route.restore': 'Dipulihkan',
  'route.poles': 'Tiang otomatis dibuat',
};

function RouteHistory({ id }: { id: string }) {
  const history = useRouteHistory(id);
  if (!history.data || history.data.length === 0) return null;
  return (
    <section className="fn-drawer__section">
      <span className="fn-label">Riwayat</span>
      <div className="fn-feed">
        {history.data.slice(0, 10).map((h) => (
          <div key={h.id} className="fn-feed__item" style={{ paddingInline: 0 }}>
            <span className="fn-status fn-status--unknown" style={{ paddingTop: 5 }}>
              <span className="fn-status__dot" />
            </span>
            <div style={{ minWidth: 0 }}>
              <div className="fn-feed__title">
                {ROUTE_ACTION_LABEL[h.action] ?? h.action}
                {h.user && (
                  <span style={{ fontWeight: 500, color: 'var(--text-secondary)' }}> · {h.user}</span>
                )}
              </div>
              <div className="fn-feed__meta">{describe(h.diff)}</div>
            </div>
            <div className="fn-feed__time">{dateTime.format(new Date(h.at))}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

function describe(diff: unknown): string {
  const d = (diff ?? {}) as Record<string, unknown>;
  if (d.changes) {
    return Object.entries(d.changes as Record<string, [unknown, unknown]>)
      .map(([k, [a, b]]) => `${k}: ${a ?? '—'} → ${b ?? '—'}`)
      .join(' · ');
  }
  if (Array.isArray(d.lengthM)) return `${metres(Number(d.lengthM[0]))} → ${metres(Number(d.lengthM[1]))}`;
  if (typeof d.created === 'number') return `${d.created} tiang, interval ${d.intervalM} m`;
  if (typeof d.polesRemoved === 'number' && d.polesRemoved > 0)
    return `${d.polesRemoved} tiang otomatis ikut dihapus`;
  if (d.from && d.to) return `${String(d.from)} → ${String(d.to)} · ${metres(Number(d.lengthM))}`;
  return '';
}
